// frontend/src/pages/CustomerDetail.jsx
//
// Stufe K der globalen Suche — eine Kundin oder ein Kunde, NUR LESEND.
//
// Plan: docs/superpowers/plans/2026-10-14-globale-suche-ausbau.md §„Stufe K",
// Fassung nach Grill K (2026-10-16). Owner-Entscheid 2026-10-15, woertlich:
// „JA, A. Mit Absprung zu den Orders in Shopify, und zum User in Shopify, und
// Cases, die dem Kunden zugeordnet sind sollten auch auf der Seite sein."
//
// 🔴 Kein Eingabefeld, kein Speichern-Knopf, kein Betrag. Wer die Stammdaten
//    aendern will, aendert sie in Shopify — die Seite ist ein Leseblatt.
//
// 🔴 UNBEKANNT IST NIE NULL. Der Bestellzaehler kommt mit DREI Zustaenden aus
//    dem Endpunkt (`orders.qualifying`: Zahl, `0`, oder `null` = die
//    Bestelltabelle kennt diesen Kunden nicht). `null` wird als „unknown"
//    gezeichnet, nie als „0" — die Regel, die dieses Repo sich mit den 42
//    falschen Nullen aus `rma_cases.customer_order_count` erarbeitet hat.

import { useCallback, useEffect, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Alert, Box, Divider, Link as MuiLink, Skeleton, Stack, Typography,
} from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import PageContainer from '../components/common/PageContainer'
import ViewHeader from '../components/ViewHeader'
import Panel from '../components/common/Panel'
import Pill from '../components/common/Pill'
import EmptyState from '../components/common/EmptyState'
import { api } from '../api/client'
import { DETAIL_RAIL_WIDTH, FS, SP } from '../theme/tokens'
import { formatBerlinDate } from '../utils/time'
import { shopifyCustomerUrl, shopifyOrderUrl } from '../utils/shopifyAdmin'
import { STATUS_CONFIG } from '../rma/constants'

/** Der Strich, wo eine Quelle nichts geliefert hat — nie eine erfundene 0. */
const STRICH = '—'

/**
 * Nur fuer Hilfsmittel sichtbarer Text — die Hausform.
 *
 * 🔴 **String-Einheiten, keine nackten Zahlen.** `styleFunctionSx` wandelt eine
 *    nackte `1` in `100 %` um: `width: 1` wird zum bildschirmfuellenden
 *    Overlay, das nur noch das veraltete `clip` unsichtbar haelt. Mit `'1px'`
 *    greifen die Umformungen nicht. Der Waechter darueber ist
 *    `components/common/srOnlyUnitlessSx.test.js`, die Vorlage
 *    `components/common/ActivityTimeline/ActivityEntryRow.jsx:573-583`.
 */
const SR_ONLY = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
}

/**
 * Die Statusfarbe des Hauses (`rma/constants.js::STATUS_CONFIG`) auf die
 * Toene von `Pill` abgebildet.
 *
 * 🔴 Die Zuordnung lebt hier und nicht in `constants.js`: dort ist die Farbe
 *    eine MUI-Vokabel (`success`/`warning`/…), hier eine Pill-Vokabel. Wer
 *    beide vermischt, bekommt ein `Pill tone="success"`, das still auf
 *    `plain` zurueckfaellt — ein Status, der wie „kein Status" aussieht.
 *    Unbekannte Faelle fallen auf `plain`, NIE auf einen erfundenen Ton.
 */
const TON = { success: 'ok', warning: 'warn', error: 'err', info: 'info', default: 'plain' }

/**
 * Eine Zeile „Beschriftung — Wert". Ein fehlender Wert ist ein Strich.
 *
 * 🔴 `overflowWrap: 'anywhere'` auf der Wertseite (R16-W4, Nachbesserung 2).
 *    `minWidth: 0` allein laesst den Kasten SCHRUMPFEN, bricht aber nichts: ein
 *    Token ohne Leerzeichen — eine 60-stellige E-Mail, ein Firmenname aus einem
 *    Wort — ist breiter als die Rail und schiebt den Dokument-Scrollbereich
 *    auf. Dieselbe Form steht in der Hausform fuer Beschriftung/Wert-Zeilen
 *    (`pages/invoicing/InvoiceDetail.jsx:786`: `textAlign: 'right'`,
 *    `minWidth: 0`, `overflowWrap: 'anywhere'`). `anywhere` statt
 *    `break-word`, weil nur `anywhere` auch die INTRINSISCHE Mindestbreite
 *    senkt — bei einem Flex-Kind ist das der Unterschied zwischen „bricht um"
 *    und „bricht um, nachdem es die Zeile schon gesprengt hat".
 */
function Zeile({ label, children }) {
  return (
    <Stack direction="row" justifyContent="space-between" gap={SP.card} sx={{ py: SP.chip }}>
      <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>{label}</Typography>
      <Typography
        component="div"
        sx={{ fontSize: FS.base, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {children}
      </Typography>
    </Stack>
  )
}

function Abschnitt({ titel, zaehler, children }) {
  return (
    <Panel>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={SP.tight}>
        <Typography sx={{ fontSize: FS.lg, fontWeight: 600 }}>{titel}</Typography>
        {zaehler}
      </Stack>
      <Divider sx={{ my: SP.tight }} />
      {children}
    </Panel>
  )
}

/** Ein externer Link mit sichtbarem Hinweis auf das Ziel (Icon + Name). */
function ShopifyLink({ href, label, children }) {
  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} (new tab)`}
      sx={{
        fontSize: FS.base, fontWeight: 500, display: 'inline-flex',
        alignItems: 'center', gap: 0.25,
      }}
    >
      {children}
      <LaunchIcon sx={{ fontSize: FS.sm }} />
    </MuiLink>
  )
}

/**
 * Ein Status als Pill: Form + Text + Farbe, nie Farbe allein (Regel 22 §2).
 *
 * 🔴 `bezug` (R16-W5, Nachbesserung 2) gibt der Pille ihren BEZUG. Eine
 *    Bestellzeile traegt ZWEI Pillen nebeneinander — Zahlung und Versand —, und
 *    beide bestehen aus demselben Baukasten (`rma/constants.js::STATUS_CONFIG`,
 *    ein Proxy mit Rueckfall). Ohne Bezug heissen sie fuer einen Screenreader
 *    nur „Paid" und „Fulfilled": nicht unterscheidbar, welche zur Zahlung und
 *    welche zum Versand gehoert.
 *
 * 🔴 **Der Bezug reist als TEXT, nicht als Attribut (R15-W1/R16-C1,
 *    Nachbesserung 3).** Die erste Fassung setzte `aria-label` auf die Pille.
 *    Das traegt hier NICHT: `Pill` rendert ohne `onClick` einen `<Box
 *    component="span">` (`components/common/Pill.jsx:181`), und ein `span` hat
 *    die implizite Rolle `generic` — ARIA 1.2 fuehrt dafuer „Name From:
 *    prohibited", das Attribut wird von Browsern nicht als Name ausgeliefert
 *    (axe-Regel `aria-prohibited-attr`). Genau diesen Fehlgriff hat das Haus an
 *    anderer Stelle selbst aufgeschrieben (`components/common/ProvenanceMark.
 *    jsx:66-80`). Ein unsichtbarer Text IN der Pille kommt dagegen aus dem
 *    INHALT und wird in jedem Rollenzweig angesagt.
 *
 *    Die sichtbare Beschriftung bleibt kurz („Paid"): WCAG 2.5.3 „Label in
 *    Name" ist erfuellt, weil der sichtbare Text im vorgelesenen mit drin
 *    steht. Der `title` bleibt fuer den Mauszeiger — er ist ausdruecklich
 *    KEIN Beleg fuer die Ansage (dieselbe Abwaegung wie `ProvenanceMark.jsx:70-75`).
 *
 *    🔴 R16-W10: `title` UND sr-only-Text auf DEMSELBEN Knoten sind genau die
 *    Kombination, die das Haus an anderer Stelle als die doppelt-ansagende
 *    beschreibt — „it cannot double-announce the way sr-only text stacked on
 *    top of it would" (`components/common/ProvenanceMark.jsx:62-64`). Reicht
 *    ein Hilfsmittel `title` auf einem rollenlosen Element doch durch, kann
 *    „Payment" UND „Payment: Paid" angesagt werden. Das ist hier der
 *    HINGENOMMENE PREIS dafuer, dass der Mausnutzer die Auskunft beim
 *    Ueberfahren sieht; verstaendlich bleibt die Ansage in beiden Faellen.
 *
 *    🔴 R16-W12: auch der Zweig OHNE Status traegt den Bezug. Sonst stehen auf
 *    einer Bestellzeile mit fehlendem Status zwei nackte Striche nebeneinander
 *    (`financial_status`/`fulfillment_status` kommen roh aus
 *    `backend/customer_orders.py`, `null` ist ein mitzaehlender Fall), und
 *    keiner der zwei sagt — vorgelesen wie beim Ueberfahren —, welcher zur
 *    Zahlung und welcher zum Versand gehoert.
 */
function StatusPill({ status, bezug }) {
  if (!status) {
    return (
      <Typography title={bezug || undefined} sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
        {bezug ? <Box component="span" sx={SR_ONLY}>{`${bezug}: `}</Box> : null}
        {STRICH}
      </Typography>
    )
  }
  const cfg = STATUS_CONFIG[status]
  return (
    <Pill tone={TON[cfg.color] || 'plain'} title={bezug || undefined}>
      {bezug ? <Box component="span" sx={SR_ONLY}>{`${bezug}: `}</Box> : null}
      {cfg.label}
    </Pill>
  )
}

export default function CustomerDetail() {
  const { id } = useParams()
  const [daten, setDaten] = useState(null)
  const [laedt, setLaedt] = useState(true)
  // 🔴 `null` = kein Fehler, `'fehlt'` = 404 (gibt es nicht), `'abruf'` = jeder
  //    andere Fehlschlag. Die zwei Lagen tragen ZWEI Anzeigen (siehe unten): nur
  //    der 404 heisst „does not exist", und nur der andere braucht einen Weg
  //    heraus. Hausform: `pages/MeldungDetail.jsx:97-100`.
  const [fehler, setFehler] = useState(null)

  const hole = useCallback(() => {
    let abgemeldet = false
    setLaedt(true)
    api.get(`/api/customers/${id}`)
      .then(({ data }) => { if (!abgemeldet) { setDaten(data); setFehler(null) } })
      .catch((e) => {
        if (abgemeldet) return
        // 🔴 NUR der 404 heisst „gibt es nicht". Jeder andere Fehlschlag (500,
        //    Netzfehler) ist ein ABRUFfehler — ein anderer Wortlaut, und mit
        //    einem Weg heraus. Ein „Try again" am 404 kann NIE gelingen: der
        //    Betreiber mit einem veralteten Lesezeichen drueckte beliebig oft
        //    und laese jedes Mal dieselbe Zeile.
        setFehler(e?.response?.status === 404 ? 'fehlt' : 'abruf')
      })
      .finally(() => { if (!abgemeldet) setLaedt(false) })
    return () => { abgemeldet = true }
  }, [id])

  useEffect(() => hole(), [hole])

  const kunde = daten?.customer
  const kundeUrl = shopifyCustomerUrl(kunde?.shopify_customer_id)
  const bestellungen = daten?.orders
  const faelle = daten?.cases

  return (
    <PageContainer>
      <ViewHeader
        // Es gibt (noch) keine Kundenliste — der Weg hierher ist der Fall
        // (Stufe K, Grill B3) oder spaeter der Suchtreffer (Stufe N1).
        //
        // 🔴 Das Etikett heisst „Back", nicht „Back to cases" (R16-W6,
        //    Nachbesserung 2). `ViewHeader` geht BEVORZUGT in der Historie
        //    zurueck und nimmt `backLink.href` nur als Rueckfall
        //    (`ViewHeader.jsx:1102`, `:431`) — der Knopf fuehrt also meist auf
        //    den Fall, aus dem man kam, und ab Stufe N1 in die Suchtrefferliste.
        //    „Back to cases" verspraeche in beiden Faellen ein Ziel, das der
        //    Knopf nicht ansteuert. Die Geschwister-Etiketten („Back to
        //    products", „Back to shipments", …) setzen eine Liste voraus, die
        //    es hier nicht gibt — ein nacktes „Back" ist die ehrliche Form.
        backLink={{ label: 'Back', href: '/rma' }}
        title={kunde ? (kunde.name || 'No name') : 'Customer'}
        // 🔴 R16-W4, zweite Flaeche: die E-Mail steht ZWEIMAL auf der Seite —
        //    in der Stammdatenzeile und hier im Kopf. `ViewHeader` nimmt einen
        //    String ODER einen Knoten (`ViewHeader.jsx:1190-1197`, „Element
        //    passes through verbatim"), und genau der Knotenweg ist hier noetig:
        //    der Stringweg rendert eine Typografie ohne Umbruchschutz, und ein
        //    unteilbarer Wert (60-stellige Adresse) schoebe dann den
        //    Dokument-Scrollbereich auf — derselbe Befund, eine Flaeche weiter.
        //    Bewusst am AUFRUFER statt im geteilten `ViewHeader`: dort haenge
        //    die Aenderung an jeder Seite des Hauses.
        //    Die Typografie ist die des Stringwegs (`variant="body2"`,
        //    `text.secondary`); den Abstand traegt die Huelle des Kopfes
        //    (`Box sx={{ mt: 0.25 }}`), deshalb hier KEIN `mt`.
        subtitle={kunde?.email ? (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {kunde.email}
          </Typography>
        ) : undefined}
        // 🔴 R16-W2: die Aktionen entstehen erst, wenn `kundeUrl` da ist.
        //    Auf dem Telefon ist die Aktionszeile eine EIGENE Zeile unter dem
        //    Titel — fehlt sie im Ladezweig, springt der Inhalt in dem Moment,
        //    in dem die Antwort eintrifft. `actionsPending` reserviert sie
        //    hoehengleich (`ViewHeader.jsx:900-921`; dieselbe Zeile tragen
        //    `ProductDetail`, `PODetail`, `VendorDetails`).
        actionsPending={laedt}
        // 🔴 `actions` ist eine Liste von OBJEKTEN, keine von Knoten
        // (`ViewHeader.jsx:672ff.`): `href`/`component`/`target`/`rel` baut der
        // Kopf selbst zum Anker um. Ein durchgereichter React-Knoten landet in
        // `...rest` und wirft „Invalid prop `type`" — genau das hat die
        // Jest-Probe vor dem ersten Lauf gefunden.
        actions={kundeUrl ? [{
          key: 'shopify',
          label: 'Open in Shopify',
          href: kundeUrl,
          component: 'a',
          target: '_blank',
          rel: 'noopener noreferrer',
          startIcon: <LaunchIcon sx={{ fontSize: FS.sm }} />,
          'aria-label': 'Open in Shopify (new tab)',
        }] : []}
      />

      {fehler === 'fehlt' && (
        // 🔴 ZWEI LAGEN, ZWEI ANZEIGEN — dieselbe Weiche wie
        // `pages/MeldungDetail.jsx:165-179`. Ein 404 ist ein ENDzustand: der
        // Kunde existiert nicht, und ein Wiederholen-Knopf fuehrte in dieselbe
        // 404. `reason="no-data"` (nicht `"error"`), weil die Flaeche nichts
        // meldet, was schiefging — dieselbe Wahl trifft
        // `components/VendorDetails.jsx:2516-2520` fuer ihren 404. Unter
        // `reason="error"` verlangt `EmptyState` ausserdem einen Weg heraus
        // und meldete hier bei jedem Aufruf einen Mangel im Entwicklermodus.
        <EmptyState
          reason="no-data"
          message="This customer does not exist."
          detail="The link may be outdated."
        />
      )}

      {fehler === 'abruf' && (
        // Jeder ANDERE Fehlschlag ist KEIN Endzustand: er nennt den Fehlschlag
        // (nicht „does not exist") und traegt den Weg heraus.
        <EmptyState
          reason="error"
          message="The customer could not be loaded."
          detail="The request failed."
          onRetry={() => hole()}
        />
      )}

      {laedt && (
        <Stack spacing={SP.card}>
          <Skeleton variant="rounded" height={140} />
          <Skeleton variant="rounded" height={200} />
        </Stack>
      )}

      {!laedt && daten && (
        <>
          {daten.erased && (
            // 🔴 R16-W3: der Wortlaut nennt, was die Seite TATSAECHLICH zeigt.
            //    „The details below are a tombstone" versprach eine
            //    Grabstein-Anzeige, die es bewusst nicht gibt: der Endpunkt
            //    verschweigt die Werte (`backend/customer_detail_api.py:187-196`
            //    — auch ein stehengebliebener Name wird nicht gezeigt), und die
            //    Seite schreibt an ihre Stelle „No name" / „—". Der Nutzer las
            //    also „die Angaben darunter sind ein Grabstein" und sah darunter
            //    Leerwerte. Die Auskunft, die er braucht — „hier ist bewusst
            //    nichts mehr, und warum" — steht jetzt da.
            <Alert severity="warning" sx={{ mb: SP.card }}>
              Customer data erased (GDPR Art. 17). Personal details are
              withheld. Orders and cases are retained and stay visible.
              Where a company was linked, its identity is retained and
              remains searchable.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: SP.md, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Hauptspalte */}
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <Stack spacing={SP.card}>
                <Abschnitt
                  titel="Orders"
                  zaehler={
                    <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                      {bestellungen.qualifying === null
                        ? 'order history unknown'
                        // 🔴 R16-W1: BEIDE Zahlen, benannt. Der Zaehler stand hier
                        //    als „N qualifying" neben einer Liste, die ALLE
                        //    Bestellungen zeigt — ein Kunde mit drei Bestellungen,
                        //    davon eine erstattet, sah drei Zeilen und daneben
                        //    „2 qualifying", ohne dass irgendwo stand, wie viele
                        //    Zeilen sichtbar sind. „N shown · M qualifying" ist
                        //    die Form, die `customer_orders.py:219` und
                        //    `docs/features/customers-crm.md:194-195` ohnehin
                        //    zusagen („beide Zahlen stehen benannt nebeneinander").
                        : `${bestellungen.shown} shown · ${bestellungen.qualifying} qualifying`}
                    </Typography>
                  }
                >
                  {/* R16-W1, zweite Haelfte: das WORT erklaeren. „qualifying"
                      stand nirgends auf dem Schirm; die Definition lebte allein
                      in `customer_orders.py` („cancelled_at IS NULL AND
                      financial_status NOT IN ('refunded','voided')"). Nur wenn
                      es die Zahl ueberhaupt gibt — im Unbekannt-Zweig traegt
                      der Leerzustand die Aussage. */}
                  {bestellungen.qualifying !== null && (
                    <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mb: SP.chip }}>
                      “Qualifying” excludes cancelled, refunded and voided orders.
                    </Typography>
                  )}
                  {bestellungen.items.length === 0 ? (
                    bestellungen.qualifying === null ? (
                      <EmptyState
                        reason="unknown"
                        message="Order history unknown"
                        detail="The order table does not know this customer. That is not the same as zero orders."
                      />
                    ) : (
                      <EmptyState reason="no-data" message="No orders for this customer yet" />
                    )
                  ) : (
                    <Stack divider={<Divider />} spacing={SP.tight}>
                      {bestellungen.items.map((b) => {
                        const url = shopifyOrderUrl(b.order_id)
                        const name = b.order_name || b.order_id
                        return (
                          <Stack
                            key={b.order_id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={SP.card}
                            sx={{ flexWrap: 'wrap' }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              {url ? (
                                <ShopifyLink href={url} label={`Open order ${name} in Shopify`}>
                                  {name}
                                </ShopifyLink>
                              ) : (
                                // Keine Kennung → kein Link, und der Grund steht
                                // daneben. Ein Link auf `…/orders/undefined`
                                // waere ein toter Link mit Anzeige.
                                <Stack direction="row" gap={SP.chip} alignItems="center">
                                  <Typography sx={{ fontSize: FS.base, fontWeight: 500 }}>{name}</Typography>
                                  <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                                    no order id — no Shopify link
                                  </Typography>
                                </Stack>
                              )}
                              <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                                {formatBerlinDate(b.created_at)}
                                {b.cancelled_at ? ` · cancelled ${formatBerlinDate(b.cancelled_at)}` : ''}
                              </Typography>
                            </Box>
                            <Stack direction="row" gap={SP.chip} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                              {/* Zwei Pillen aus demselben Baukasten — ohne
                                  Bezug sind sie fuer einen Screenreader nicht
                                  auseinanderzuhalten (R16-W5). */}
                              <StatusPill status={b.financial_status} bezug="Payment" />
                              <StatusPill status={b.fulfillment_status} bezug="Fulfillment" />
                            </Stack>
                          </Stack>
                        )
                      })}
                    </Stack>
                  )}
                  {bestellungen.truncated && (
                    <Typography sx={{ mt: SP.tight, fontSize: FS.xs, color: 'text.secondary' }}>
                      Showing the newest {bestellungen.shown}. This customer has more orders.
                    </Typography>
                  )}
                </Abschnitt>

                <Abschnitt
                  titel="Cases"
                  zaehler={
                    <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                      {faelle.shown}
                    </Typography>
                  }
                >
                  {faelle.items.length === 0 ? (
                    <EmptyState reason="no-data" message="No cases for this customer" />
                  ) : (
                    <Stack divider={<Divider />} spacing={SP.tight}>
                      {faelle.items.map((f) => (
                        <Stack
                          key={f.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={SP.card}
                          sx={{ flexWrap: 'wrap' }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <MuiLink
                              component={RouterLink}
                              to={`/rma/${f.id}`}
                              sx={{ fontSize: FS.base, fontWeight: 500 }}
                            >
                              {f.case_number}
                            </MuiLink>
                            <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                              {formatBerlinDate(f.created_at)}
                            </Typography>
                          </Box>
                          <StatusPill status={f.status} />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  {faelle.truncated && (
                    <Typography sx={{ mt: SP.tight, fontSize: FS.xs, color: 'text.secondary' }}>
                      Showing the newest {faelle.shown}. This customer has more cases.
                    </Typography>
                  )}
                </Abschnitt>
              </Stack>
            </Box>

            {/* Seitenspalte — im DOM NACH der Hauptspalte, damit sie auf dem
                Telefon darunter steht (Pattern Library §Detail Page). */}
            <Box
              data-testid="detail-rail"
              sx={{ width: { xs: '100%', md: DETAIL_RAIL_WIDTH.narrow }, flexShrink: 0 }}
            >
              <Stack spacing={SP.card}>
                <Abschnitt titel="Customer">
                  <Zeile label="Name">
                    {kunde.name || <Typography component="span" sx={{ color: 'text.secondary' }}>No name</Typography>}
                  </Zeile>
                  <Zeile label="Email">{kunde.email || STRICH}</Zeile>
                  <Zeile label="Company">{kunde.company_name || STRICH}</Zeile>
                  <Zeile label="Locale">{kunde.locale || STRICH}</Zeile>
                  <Zeile label="Type">{kunde.customer_type || STRICH}</Zeile>
                  <Zeile label="First order">{formatBerlinDate(kunde.first_order_at)}</Zeile>
                  <Zeile label="Last order">{formatBerlinDate(kunde.last_order_at)}</Zeile>
                  <Zeile label="Shopify">
                    {kundeUrl
                      // 🔴 Eigener Name, nicht derselbe wie der Kopf-Knopf: zwei
                      // Links mit demselben zugaenglichen Namen sind fuer einen
                      // Screenreader zwei gleich benannte Ziele — und fuer jede
                      // Probe, die ueber den Namen sucht, eine Mehrdeutigkeit.
                      ? <ShopifyLink href={kundeUrl} label="Open customer in Shopify">Open</ShopifyLink>
                      : (
                        <Stack direction="row" gap={SP.chip} alignItems="center" justifyContent="flex-end">
                          <Typography component="span" sx={{ color: 'text.secondary' }}>{STRICH}</Typography>
                          <Typography component="span" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                            no customer id
                          </Typography>
                        </Stack>
                      )}
                  </Zeile>
                </Abschnitt>

                {/* 🔴 NUR bei Kopplung (Grill W6): `b2b_customers.customer_id`
                    schreibt im ganzen Repo niemand, der Block ist praktisch
                    ueberall leer. Ein leerer Kasten ist keine Auskunft — er
                    entfaellt ganz. */}
                {daten.b2b && (
                  <Abschnitt titel="B2B">
                    <Zeile label="Company">{daten.b2b.company_name || STRICH}</Zeile>
                    <Zeile label="VAT number">{daten.b2b.vat_number || STRICH}</Zeile>
                    <Zeile label="Status">{daten.b2b.status || STRICH}</Zeile>
                    <Zeile label="VAT check">{daten.b2b.vat_validation_status || STRICH}</Zeile>
                  </Abschnitt>
                )}
              </Stack>
            </Box>
          </Box>
        </>
      )}
    </PageContainer>
  )
}
